import * as React from 'react';
import { ElementType, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableCellBaseProps,
  Typography,
} from '@mui/material';

import { ContentBox } from 'components/ContentBox';

import styles from './styles.module.scss';
import { CollapsibleRowDocument } from './collapsible-row-document';

export type Column = {
  title: string;
  component?: ReactNode;
};

export type Cell = {
  key: string;
  sort: number;
  component: ElementType<TableCellBaseProps> | undefined;
  scope: string | undefined;
  isBold?: boolean | undefined;
  data: ReactNode;
};

export type Row = {
  id: string;
  key: string;
  cells: Cell[];
};

export type DashboardTableProps = {
  columns: Column[];
  rows: Row[];
  isSearch?: boolean;
  rowHeight?: number;
  withPagination?: boolean;
  onClickRow?: (id: string) => void;
  defaultRowsPerPage?: number;
  listOfAllCandidateDocuments?: {
    columns: Column[];
    rows: Row[];
  };
};

const rowsPerPageEmpty = 3;

export const DriverDocumentsTable = ({
  columns,
  rows,
  isSearch,
  rowHeight = 65,
  withPagination = true,
  defaultRowsPerPage = 10,
  listOfAllCandidateDocuments,
}: DashboardTableProps) => {
  const [page] = React.useState(0);
  const [rowsPerPage] = React.useState(
    withPagination ? defaultRowsPerPage : -1
  );

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <TableContainer component={ContentBox}>
      <Table
        sx={{ minWidth: 900 }}
        aria-label="simple table"
        className={styles.root}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '10px', padding: 0 }}></TableCell>
            {columns.map((column, index) => (
              <TableCell
                align={index === columns.length - 1 ? 'right' : 'left'}
                key={column.title}
              >
                {column.component ? column.component : column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!isSearch &&
            (rowsPerPage > 0
              ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : rows
            ).map((row) => (
              <CollapsibleRowDocument
                key={row.id}
                row={row}
                columns={columns}
                listOfAllCandidateDocuments={listOfAllCandidateDocuments}
              />
            ))}
          {!isSearch && !rows.length && (
            <TableRow style={{ height: rowHeight * rowsPerPageEmpty }}>
              <TableCell colSpan={6} align="center">
                <Typography className={styles.noData}>
                  No pending requests
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!isSearch && emptyRows > 0 && (
            <TableRow style={{ height: rowHeight * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
